import React, { type PropsWithChildren } from 'react'
import { TimelineUser } from '../../../shared/webcomponents/react-wrappers/TimelineUser'
import i18next from 'i18next'
import type { Profile } from '../user/Profile'

interface UsersAndTeamsProps {
    users: number[]
    teams: number[]
    getUserProfile: (userId: number) => Profile
    onUserClick: (userId: number) => void
    getTeamName: (teamId: number) => string
}

function UserLabel(user: Profile, onUserClick: (userId: number) => void): JSX.Element {
    const deletedUserTranslation = i18next.t('appcore:User.unknown')
    const userProfileIsClickable = user && !user?.isDeleted
    return (
        <TimelineUser
            key={user?.userId}
            name={user?.isDeleted ? deletedUserTranslation : user?.name || deletedUserTranslation}
            type={userProfileIsClickable ? 'link' : 'plain'}
            onClick={() => (userProfileIsClickable ? onUserClick(user.userId) : undefined)}
        />
    )
}

function TeamLabel(teamName: string): JSX.Element {
    return <b className="AppCore__ui__UsersAndTeams-boldText">{teamName}</b>
}

/**
 * LanguageSensitiveListFormatter is a React component that formats a list of children elements
 * with language-sensitive conjunctions. It handles the following cases:
 *
 * - If there are no children, it returns an empty fragment.
 * - If there is only one child, it returns that child directly.
 * - If there are multiple children, it formats them with commas and a conjunction (e.g., "and") before the last item.
 *
 * The conjunction is retrieved using the i18next library for localization support.
 *
 * @param {PropsWithChildren<{}>} props - The props for the component, which includes children elements.
 * @returns {JSX.Element} The formatted list of children elements.
 */
function LanguageSensitiveListFormatter({ children }: PropsWithChildren<{}>): JSX.Element {
    const items = React.Children.toArray(children)

    if (items.length === 0) {
        return <></>
    }

    if (items.length === 1) {
        return <>{items[0]}</>
    }

    const elements = items.reduce<JSX.Element[]>((acc, item, i) => {
        if (i < items.length - 2) {
            return [
                ...acc,
                <React.Fragment key={`item-${i}`}>{item}</React.Fragment>,
                <span key={`comma-${i}`}>, </span>,
            ]
        }

        if (i === items.length - 2) {
            const lastConjunction = i18next.t('shared:common.and')
            return [
                ...acc,
                <React.Fragment key={`item-${i}`}>{item}</React.Fragment>,
                <span key={`and-${i}`}> {lastConjunction} </span>,
            ]
        }

        return [...acc, <React.Fragment key={`item-${i}`}>{item}</React.Fragment>]
    }, [])

    return <>{elements}</>
}

export const UsersAndTeams = (props: UsersAndTeamsProps): JSX.Element => {
    const { users, teams, getUserProfile, getTeamName } = props
    const usersProfiles = users ? users.map((userId) => getUserProfile(userId)) : []
    const teamsNames = teams ? teams.map((teamId) => getTeamName(teamId) ?? i18next.t('appcore:Team.unknown')) : []

    return (
        <LanguageSensitiveListFormatter>
            {[usersProfiles.map((user) => UserLabel(user, props.onUserClick)), teamsNames.map(TeamLabel)]}
        </LanguageSensitiveListFormatter>
    )
}
